import {ComponentRef, ElementRef, inject, Injectable, Type} from '@angular/core';
import {
    DynamicComponent,
    DynamicComponentOptions,
    NgDynamicComponentsService
} from '../../service/ng-dynamic-components.service';
import {Popup, PopupTrigger} from '@mescius/wijmo.input';
import {PopupPosition} from '@mescius/wijmo';
import {CancellableMouseOverWithDelay} from './cancellable-mouse-over-with.delay';

export type BeforeShowHandler<T extends DynamicComponent, O> = (tt: ComponentTooltip<T>, element: HTMLElement, init: O, data: any) => boolean | undefined | void | Promise<boolean | void | undefined>;
export type AfterHideHandler<T extends DynamicComponent, O> = (tt: ComponentTooltip<T>, componentRef: T, element: HTMLElement, data: any) => void | Promise<void>;

/**
 * Interface representing the options for configuring a tooltip associated with a dynamic component.
 *
 * @template T The type of the dynamic component that the tooltip creates when showing up.
 * @template O The type of the input data used for initializing the tooltip (via ComponentRef.setInput()).
 */
export interface ComponentTooltipOptions<T extends DynamicComponent, O = any> {
    input?: O;
    width?: string;
    height?: string;
    maxHeight?: string;
    padding?: string;
    isResizable?: boolean
    isDraggable?: boolean;
    beforeShow?: BeforeShowHandler<T, O>;
    afterHide?: AfterHideHandler<T, O>;
}

interface EventWithTarget {
    target: HTMLElement;
}

/**
 * Represents a tooltip hook that can dynamically attach to HTML elements, providing component-based tooltips.
 * The tooltips are managed using mouse-over and mouse-out events and can be customized with a variety of options.
 * (Note - this implementation is not using Wijmo's Tooltip class but rather a custom implementation.)
 *
 * The type of the tooltip's content is determined by the generic type parameter `T`, which extends `DynamicComponent`.
 * The `O` generic parameter defines the type of the input options passed to the dynamic component.
 *
 * THIS IS NOT AN ANGULAR COMPONENT CLASS (despite its similar name). Therefore, it cannot make use
 * of component / service dependency injection (get services via constructor parameters).
 */
export class ComponentTooltip<T extends DynamicComponent = DynamicComponent, O = any> {

    // component class which will be instantiated as a dynamic component
    componentType!: Type<T>;

    // options for the tooltip (note that the 'input' option represents the options handed over to the component)
    options: ComponentTooltipOptions<T, O> = {};

    private readonly mouseOverListeners = new WeakMap<HTMLElement, CancellableMouseOverWithDelay>();

    private ttMouseOverHandler = this.ttMouseOver.bind(this);
    private ttMouseOutHandler = this.ttMouseOut.bind(this);

    private currentPopup: Popup | undefined;

    constructor(private readonly dynCompSvc: NgDynamicComponentsService) {
    }

    setTooltip(element: HTMLElement | ElementRef, data?: any) {
        const el = element instanceof ElementRef ? element.nativeElement : element;
        if (!this.mouseOverListeners.has(el)) {
            this.mouseOverListeners.set(el, new CancellableMouseOverWithDelay(el, {
                mouseOver: this.ttMouseOverHandler,
                mouseOut: this.ttMouseOutHandler,
                data: data
            }));
        }
    }

    removeTooltip(element: HTMLElement | ElementRef) {
        const el = element instanceof ElementRef ? element.nativeElement : element;
        const listener = this.mouseOverListeners.get(el);
        if (listener) {
            this.mouseOverListeners.delete(el);
            listener.destroy();
        }
    }

    async show(element: HTMLElement | ElementRef) {
        const event = {target: element instanceof ElementRef ? element.nativeElement : element};
        await this.ttMouseOver(event);
    }

    private async ttMouseOver(event: MouseEvent | EventWithTarget) {
        this.cancelCurrentPopup();

        const { target, regInfo } = this.findRegInfo(event);
        const init = structuredClone(this.options.input) ?? {} as O;

        if (target == null) {
            console.warn('No target element found for tooltip. Skipping show.');
            return;
        }

        // Evaluate / assign init values and possibly cancel before showing the tooltip
        const beforeShowResult = await this.options?.beforeShow?.call(undefined, this, target, init, regInfo?.data);
        if (beforeShowResult === false) {
            return;
        }

        const popupEl = document.createElement('div');
        popupEl.style.margin = '0 10px';
        popupEl.style.padding = '2px';

        const dynCompOpts: DynamicComponentOptions<O> = {
            hostElement: popupEl,
            input: init
        }
        const componentRef = await this.dynCompSvc.createComponent(this.componentType, dynCompOpts);

        const popup = new Popup(popupEl, {
            owner: target,
            position: PopupPosition.RightTop,
            showTrigger: PopupTrigger.None,
            hideTrigger: PopupTrigger.Blur | PopupTrigger.LeavePopup | PopupTrigger.LeaveOwner | PopupTrigger.Leave,
            fadeIn: false,
            fadeOut: false,
            isResizable: this.options.isResizable ?? false,
            isDraggable: this.options.isDraggable ?? false,
            resizing: (p: Popup) => p.hideTrigger = PopupTrigger.Blur,
            hidden: async () => await this.onPopupHidden(componentRef, target),
        });
        popup.hostElement.addEventListener('click', (e: MouseEvent) => popup.hideTrigger = PopupTrigger.Blur);
        popup.hostElement.style.width = this.options.width ?? 'auto';
        popup.hostElement.style.height = this.options.height ?? 'auto';
        popup.hostElement.style.maxHeight = this.options.maxHeight ?? '';
        popup.hostElement.style.overflow = 'auto';
        popup.hostElement.style.padding = this.options.padding ?? '0';
        this.currentPopup = popup;
        popup.show();
    }

    private findRegInfo(event: MouseEvent | EventWithTarget): { target?: HTMLElement, regInfo?: CancellableMouseOverWithDelay } {
        const target =  event.target as HTMLElement;
        const relatedTarget = event instanceof MouseEvent ? event.relatedTarget as HTMLElement : undefined;

        const regInfoForTarget = this.mouseOverListeners.get(target);
        if (regInfoForTarget) {
            return { target, regInfo: regInfoForTarget };
        } else {
            const relatedRegInfo = relatedTarget ? this.mouseOverListeners.get(relatedTarget) : undefined;
            if (relatedRegInfo) {
                return {target: relatedTarget, regInfo: relatedRegInfo};
            } else {
                return {target: relatedTarget ?? target, regInfo: undefined};
            }
        }
    }

    private async onPopupHidden(componentRef: ComponentRef<T>, element: HTMLElement) {
        try {
            const data = this.mouseOverListeners.get(element)?.data;
            const retval = this.options?.afterHide?.call(undefined, this, componentRef.instance, element, data);
            if (retval instanceof Promise) {
                await retval;
            }
        } finally {
            componentRef.destroy();
            this.currentPopup = undefined;
        }
    }

    private ttMouseOut(event: MouseEvent) {
        // this.currentPopup?.hide();
    }

    private cancelCurrentPopup() {
        if (this.currentPopup) {
            this.currentPopup.hide();
            this.currentPopup = undefined;
        }
    }
}

/**
 * Service for creating and managing component-based tooltips dynamically.
 * This service uses the NgDynamicComponentsService to generate tooltips
 * as dynamic components and provides methods to configure them.
 */
@Injectable({ providedIn: 'root' })
export class ComponentTooltipService {

    private readonly dynCompSvc = inject(NgDynamicComponentsService);

    constructor() {
    }

    /**
     * Creates a new instance of a `ComponentTooltip` with the given component type and options.
     *
     * @param {Type<T>} componentType - The type of the dynamic component to be created.
     * @param {ComponentTooltipOptions<T, O>} [options] - Optional configuration for the tooltip and component.
     * @return {ComponentTooltip<T, O>} A new `ComponentTooltip` instance with the specified type and options.
     */
    create<T extends DynamicComponent, O>(componentType: Type<T>, options?: ComponentTooltipOptions<T, O>): ComponentTooltip<T, O> {
        const tooltip = new ComponentTooltip<T, O>(this.dynCompSvc);
        tooltip.componentType = componentType;
        tooltip.options = options ?? {};
        return tooltip;
    }
}
